import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { BrevoList, useBrevoFolders, useCreateBrevoList, useUpdateBrevoList } from '../../queries/brevoLists';

interface BrevoListFormDialogProps {
  list: BrevoList | null; // null = create
  onClose: () => void;
}

// Only Name is here (plus Folder, create-only) - the old admin's create/edit
// forms also collected a description and a list type, but its API never
// persisted either (Brevo has no such fields for a list, and there was no
// backing DB table); porting those fields would just carry the lie forward.
// listType shown elsewhere is a read-only classification computed from the
// name, not a saved value. Folder is real and required by Brevo - the old
// admin's form never collected one, so its "Create New List" 400s in
// production today ("Folder id is missing").
export function BrevoListFormDialog({ list, onClose }: BrevoListFormDialogProps) {
  const [name, setName] = useState(list?.name ?? '');
  const [folderId, setFolderId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const { data: folders = [] } = useBrevoFolders();
  const create = useCreateBrevoList();
  const update = useUpdateBrevoList();
  const isPending = create.isPending || update.isPending;

  const handleSave = () => {
    setError(null);
    const onSettled = {
      onSuccess: onClose,
      onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')),
    };
    if (list) {
      update.mutate({ id: list.id, name }, onSettled);
    } else if (folderId !== '') {
      create.mutate({ name, folderId }, onSettled);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{list ? 'Edit list' : 'Create list'}</Dialog.Title>

          <div className="mt-4">
            <label htmlFor="brevo-list-name" className="mb-1 block text-sm font-medium">
              Name
            </label>
            <input
              id="brevo-list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              autoFocus
            />
          </div>

          {!list && (
            <div className="mt-3">
              <label htmlFor="brevo-list-folder" className="mb-1 block text-sm font-medium">
                Folder
              </label>
              <select
                id="brevo-list-folder"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">Select a folder…</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || !name.trim() || (!list && folderId === '')}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
